<script lang="ts">
	import { browser } from '$app/environment';
	import type { KeepExploringCategory } from '$lib/api/velatlas/response.types';
	import { createLazyLoadAttachment } from '$lib/attachments/lazyLoad';
	import FlexibleImageCollage from '$lib/components/ui/FlexibleImageCollage.svelte';
	import NativeText from '$lib/components/ui/NativeText.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { CityFeaturePreview } from '$lib/features/cityFeature/cityFeature.type';
	import { CITY_FEATURE_TYPE_ICON_MAP } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import type { RoutePreview } from '$lib/features/route/route.type';
	import type { TourStopPreview } from '$lib/features/tour/stop/tourStopPreview';
	import { metersToFormattedDistance } from '$lib/utils/geo.util';
	import { getUniqueImages } from '$lib/utils/image.util';
	import { secondsToFormattedHourMinutes } from '$lib/utils/time.util';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import type { Component, Snippet } from 'svelte';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconClockTimeFourOutline from '~icons/mdi/clock-time-four-outline';
	import IconMapMarkerPath from '~icons/mdi/map-marker-path';

	interface CommonRouteFields {
		why: string;
		categories: string[];
		route: RoutePreview;
		stops: TourStopPreview[];
		feature: CityFeaturePreview;
	}

	const {
		items,
		location,
		language,
		keepExploring,
		onExtraStats,
		HeaderSnippet
	}: {
		items: CommonRouteFields[];
		location: PlaceLocation;
		language: Language;
		keepExploring: KeepExploringCategory[];
		onExtraStats?: (featureId: string) => { title: string; Icon: Component; text: string; textColour: string }[];
		HeaderSnippet: Snippet;
	} = $props();

	let loadedMaps = $state<Record<string, boolean>>({});

	function getMapObserver(id: string) {
		return createLazyLoadAttachment(() => {
			loadedMaps[id] = true;
		}, '200px');
	}
</script>

{@render HeaderSnippet()}
<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<ul class="grid grid-cols-1 gap-8">
		{#each items as item, i (item.feature.id)}
			<li>
				{@render RideCard(item, i === 0)}
			</li>
		{/each}
	</ul>

	<KeepExploringSection categories={keepExploring} {location} />
</div>

{#snippet RideCard(ride: CommonRouteFields, isEagerImageLoad: boolean)}
	{const cityFeatureNav = $derived<NavLink>(
		UrlUtil.toCityFeature({ location, type: ride.feature.type, featureSlug: ride.feature.slug })
	)}
	{const images = $derived<CoverImage[]>(getUniqueImages(ride.stops.flatMap((s) => s.images)))}
	{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[ride.feature.type])}

	<a
		class="group relative flex flex-col overflow-hidden rounded-box border border-base-content/5 bg-base-200 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl lg:flex-row"
		aria-label="View {ride.feature.englishName} route"
		href={`/${cityFeatureNav.href}`}
		data-sveltekit-preload-data={cityFeatureNav.preloadData}
		data-sveltekit-preload-code={cityFeatureNav.preloadCode}
	>
		<div class="relative h-56 w-full shrink-0 overflow-hidden lg:h-auto lg:w-2/5">
			<div class="absolute inset-0 h-full w-full">
				{#if images.length > 0}
					<FlexibleImageCollage {images} isEagerLoad={isEagerImageLoad} />
				{:else}
					<div class="flex h-full w-full items-center justify-center bg-base-300 text-base-content/20">
						<Icon class="text-6xl" />
					</div>
				{/if}
			</div>
		</div>

		<div class="flex flex-1 flex-col justify-between p-6 md:p-8">
			{@render BodyContent()}
		</div>
	</a>

	{#snippet BodyContent()}
		<div>
			<div class="mb-4 flex flex-col gap-1">
				{@render Header()}
			</div>

			<div class="md:text-md mb-6 rounded-box border-l-4 border-accent/90 bg-accent/30 p-4 text-sm text-accent-content">
				<i>{ride.why}</i>
			</div>

			<div class="h-[42vh] w-full lg:h-[27vh]">
				{@render Map()}
			</div>
		</div>

		<div class="divider"></div>

		<div class="mt-auto flex flex-wrap items-center gap-6 text-sm font-medium">
			{@render RouteStat({
				title: 'Total Distance',
				Icon: IconMapMarkerPath,
				text: metersToFormattedDistance(ride.route.distanceMeters),
				textColour: 'text-base-content'
			})}
			{@render RouteStat({
				title: 'Est. Duration',
				Icon: IconClockTimeFourOutline,
				text: secondsToFormattedHourMinutes(ride.route.durationSeconds),
				textColour: 'text-base-content'
			})}
			{#if onExtraStats}
				{const extraStats = $derived(onExtraStats(ride.feature.id))}
				{#each extraStats as stat (stat.title)}
					{@render RouteStat(stat)}
				{/each}
			{/if}

			<span class="btn ml-auto inline-flex w-full font-mono btn-primary">
				view route <IconArrowRight class="transition-transform group-hover:translate-x-1" />
			</span>
		</div>

		{#snippet Header()}
			<div class="flex flex-wrap gap-2 text-xs font-bold tracking-widest text-base-content/70">
				{#each ride.categories as category, i (category)}
					{const isLast = $derived<boolean>(i === ride.categories.length - 1)}
					<span>{category.replaceAll('_', ' ').toUpperCase()}</span>
					{#if !isLast}
						<span>•</span>
					{/if}
				{/each}
			</div>
			<h2 class="text-2xl transition-colors group-hover:text-primary md:text-3xl">
				{ride.feature.englishName}
			</h2>

			{#if ride.feature.nativeName}
				<NativeText
					classes="text-base-content/70 italic"
					languageCode={language.code}
					isLanguageRTL={language.rtl}
					text={ride.feature.nativeName}
				/>
			{/if}
		{/snippet}

		{#snippet Map()}
			{const markers = $derived(
				ride.stops.map((stop, index) => ({
					coordinates: stop.coordinates,
					label: index + 1,
					colour: '#FFFFFF',
					image: stop.images?.[0] ? UrlUtil.getFullImageUrl(stop.images[0]) : undefined
				}))
			)}
			<div {@attach getMapObserver(ride.feature.id)} class="relative h-full w-full overflow-hidden rounded-box bg-base-200">
				{#if browser && loadedMaps[ride.feature.id]}
					{#await import('$lib/components/ui/map/SimpleRouteMap.svelte') then { default: SimpleRouteMap }}
						<SimpleRouteMap {markers} />
					{/await}
				{:else}
					<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
						<span class="loading mr-2 loading-spinner text-primary"></span>
						<span>Loading map of {markers.length} stops...</span>
					</div>
				{/if}
			</div>
		{/snippet}

		{#snippet RouteStat(stat: { title: string; Icon: Component; text: string; textColour: string })}
			<div class="flex items-center gap-2 font-mono {stat.textColour}" title={stat.title}>
				<stat.Icon class="text-lg" />
				<span>{stat.text}</span>
			</div>
		{/snippet}
	{/snippet}
{/snippet}
